import React, { useState, useEffect } from "react";
import { Circles } from "react-loader-spinner";
import baseUrl from "../../../../api/baseUrl";
import ModalHeader from "./ModalHeader";
import ProfileHeader from "./ProfileHeader";
import TabNavigation from "./TabNavigation";
import GeneralTab from "./GeneralTab";
import MedicalTab from "./MedicalTab";
import ExaminationTab from "./ExaminationTab";
import DiagnoseTab from "./DiagnoseTab";
import InvoicesTab from "./InvoicesTab";

interface PatientViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientData: any;
  loading: boolean;
  theme?: "light" | "dark";
  onEdit?: (id: string, e: React.MouseEvent) => void;
  onAddTreatment?: (id: string, e: React.MouseEvent) => void;
  onDelete?: (id: string, e: React.MouseEvent) => void;
  canDelete?: boolean;
}

const PatientViewModal: React.FC<PatientViewModalProps> = ({
  isOpen,
  onClose,
  patientData,
  loading,
  theme = "light",
  onEdit,
  onAddTreatment,
  onDelete,
  canDelete = false,
}) => {
  const [activeTab, setActiveTab] = useState<"general" | "medical" | "examination" | "diagnose" | "invoices">("general");
  const [examinationData, setExaminationData] = useState<any[]>([]);
  const [invoicesData, setInvoicesData] = useState<any[]>([]);
  const [loadingExamination, setLoadingExamination] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    if (isOpen && patientData?._id) {
      // Fetch examination data
      setLoadingExamination(true);
      baseUrl
        .get("/patient/findTreatmentByPid", {
          headers: {
            patientid: patientData._id,
          },
        })
        .then((response: any) => {
          setExaminationData(response.data.data || []);
        })
        .catch((e: any) => {
         
          setExaminationData([]);
        })
        .finally(() => {
          setLoadingExamination(false);
        });

      // Fetch invoices data
      setLoadingInvoices(true);
      baseUrl
        .get("/payment/getPaymentByPatient", {
          headers: {
            patientid: patientData._id,
          },
        })
        .then((response: any) => {
          setInvoicesData(response.data.data || []);
        })
        .catch((e: any) => {
          console.log(e);
          setInvoicesData([]);
        })
        .finally(() => {
          setLoadingInvoices(false);
        });
    }
  }, [isOpen, patientData?._id]);

  if (!isOpen) return null;

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return <GeneralTab patientData={patientData} theme={theme} />;
      case "medical":
        return <MedicalTab patientData={patientData} theme={theme} />;
      case "examination":
        return (
          <ExaminationTab
            examinationData={examinationData}
            loading={loadingExamination}
            theme={theme}
          />
        );
      case "diagnose":
        return (
          <DiagnoseTab
            examinationData={examinationData}
            loading={loadingExamination}
            theme={theme}
          />
        );
      case "invoices":
        return (
          <InvoicesTab
            invoicesData={invoicesData}
            loading={loadingInvoices}
            theme={theme}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center px-2 sm:px-4 pb-2 sm:pb-4" style={{ margin: 0, top: 0, paddingTop: 0 }} onClick={onClose}>
      <div className={`rounded-lg sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col transition-colors ${
        theme === "dark" ? "bg-slate-800" : "bg-white"
      }`} style={{ margin: 0 }} onClick={(e) => e.stopPropagation()}>
        <ModalHeader onClose={onClose} theme={theme} />

        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Circles height="40" width="40" color="#6366f1" ariaLabel="loading" />
            </div>
          ) : patientData ? (
            <>
              <ProfileHeader
                patientData={patientData}
                theme={theme}
                onEdit={onEdit}
                onAddTreatment={onAddTreatment}
                onDelete={onDelete}
                canDelete={canDelete}
                onClose={onClose}
              />

              <TabNavigation
                activeTab={activeTab}
                onTabChange={setActiveTab}
                theme={theme}
              />

              {renderTabContent()}
            </>
          ) : (
            <div className="text-center py-12">
              <p className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>No patient data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientViewModal;
